// Compliance Sandbox — interactive drag/drop overlay for the Elevation panels.
// Renders a positioned HTML overlay on top of the sideSvg output so that every
// pallet layer becomes draggable. Rules the user asked for:
//   • Floor pallet drag  = whole stack moves as one unit
//   • Layer 2+ drag      = only that single box moves
//   • Cross-lane drag    = drop on the OTHER elevation panel = lane switch
//   • NO REVERT          = accept every drop, even if it exceeds legal limits
//   • Reactive physics   = axle + L/R telemetry recompute after every drop
//   • Smart reflow       = arrangement columns are laid out sequentially from
//                          the container nose, so gaps auto-compact.
import { useState } from "react";
import { sideSvg } from "./loadDiagram";

// SVG viewBox constants shared with sideSvg (loadDiagram.jsx). If those change,
// keep them in lock-step here so the overlay stays flush with the drawing.
const SVG_LEN = 1000;
const SVG_VB_H = 115;
const SVG_YB = 105;
const SVG_YT = 5;

// Compute a positioned rect (percent of the SVG viewBox) for every INDIVIDUAL
// pallet layer in the given lane. Layers are keyed by (arr_index, layer_index),
// so a click / drag can be traced back to the exact arrangement cell.
//
// NOTE on `arr_index`: build_manual_plan tags every placed stack with its
// arr_index (its position inside the arrangement's lane list). The auto packer
// (build_plan) does NOT — its placements have no arr_index at all. We fall back
// to the stack's position AFTER sorting the lane by `start_in`, which is the
// exact order _seedArrangement pushes stacks into the arrangement. Both paths
// therefore agree on the arr_index a drag payload should carry.
export function computeLayerRects(placement, cont_l, cont_h, laneId) {
  if (!placement?.length || !cont_l || !cont_h) return [];
  const sx = SVG_LEN / cont_l;
  const sh = (SVG_YB - SVG_YT) / cont_h;
  const rects = [];
  const laneStacks = placement
    .filter((p) => p.lane === laneId || p.lane === "AB")
    .slice()
    .sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
  laneStacks.forEach((p, laneIdx) => {
    const arrIdx = p.arr_index != null ? p.arr_index : laneIdx;
    const heights = (p.layer_heights_in && p.layer_heights_in.length)
      ? p.layer_heights_in : Array(p.layers || 1).fill(p.height_in || cont_h / Math.max(1, p.layers || 1));
    const lps = (p.layer_products && p.layer_products.length) ? p.layer_products : [p.product_code];
    const lal = p.layer_along_in || [];
    const baseW = Math.max(0, (p.end_in - p.start_in)) * sx;
    let cum = 0;
    for (let i = 0; i < heights.length; i++) {
      const h = heights[i] || 0;
      const yBottomSvg = SVG_YB - cum * sh;
      cum += h;
      const yTopSvg = SVG_YB - cum * sh;
      const lw = (lal[i] != null && lal[i] > 0) ? lal[i] * sx : baseW;
      rects.push({
        arr_index: arrIdx,
        layer_index: i,
        total_layers: heights.length,
        is_floor: i === 0,
        lane: laneId,
        product_code: lps[i] || p.product_code,
        left_pct: (p.start_in * sx / SVG_LEN) * 100,
        top_pct: (Math.max(SVG_YT, yTopSvg) / SVG_VB_H) * 100,
        width_pct: (lw / SVG_LEN) * 100,
        height_pct: (Math.max(1, yBottomSvg - Math.max(SVG_YT, yTopSvg)) / SVG_VB_H) * 100,
      });
    }
  });
  return rects;
}

// Sandbox elevation panel: draws the normal sideSvg (visuals unchanged) then
// paints an HTML overlay on top so each layer is a draggable box. The parent
// container is the drop target — it maps the drop's mouse position to an
// along-length coordinate (inches) and hands the payload to the parent
// controller which mutates the arrangement.
export function SandboxElevationPanel({ plan, cmap, lane, onSandboxMove, testid }) {
  const [dragging, setDragging] = useState(null);   // {lane, arr_index, layer_index, is_floor}
  const [dropHint, setDropHint] = useState(false);
  const cont = plan?.container || {};
  const cl = cont.internal_length_in || 1;
  const ch = cont.internal_height_in || 1;
  const laneLabel = lane === "B" ? "Right Lane (from Rear)" : "Left Lane (from Rear)";
  const svg = plan?.placement?.length
    ? sideSvg(plan.placement, cmap, lane, cl, ch, { svgClass: "w-full h-full block" })
    : null;
  const rects = plan?.placement?.length ? computeLayerRects(plan.placement, cl, ch, lane) : [];

  const onDragStart = (e, r) => {
    const payload = { source: "sandbox", lane: r.lane, arr_index: r.arr_index, layer_index: r.layer_index, is_floor: r.is_floor };
    try { e.dataTransfer.setData("text/plain", JSON.stringify(payload)); } catch { /* Firefox needs some data */ }
    e.dataTransfer.effectAllowed = "move";
    setDragging(payload);
  };
  const onDragEnd = () => { setDragging(null); setDropHint(false); };
  const onDragOver = (e) => {
    // Only accept sandbox drags (peek at types — payload is set on dragStart)
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dropHint) setDropHint(true);
  };
  const onDragLeave = () => setDropHint(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDropHint(false);
    let src;
    try { src = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    if (!src || src.source !== "sandbox") return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const py = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    // Reverse the SVG viewBox mapping to real container coordinates (inches):
    //   drop_along = px * cont_l           (front is 0 in, doors is cont_l)
    //   drop_h     = (1 - py) * cont_h     (floor is 0 in, roof is cont_h)
    const drop_along_in = px * cl;
    const drop_h_in = (1 - py) * ch;
    onSandboxMove(src, lane, drop_along_in, drop_h_in);
    setDragging(null);
  };

  return (
    <div className={`relative flex-1 min-h-0 rounded-lg border p-2 flex flex-col transition-all ${dropHint ? "border-2 border-dashed border-emerald-400 bg-emerald-500/5 shadow-[0_0_18px_rgba(16,185,129,0.25)]" : "border-slate-800 bg-slate-900/80"}`} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1 shrink-0 flex items-center justify-between">
        <span>Elevation · {laneLabel} · <span className="text-emerald-300 font-bold">SANDBOX</span></span>
        <span className="text-slate-500 normal-case font-mono text-[9px]">
          floor pallet = drag whole stack · upper layer = drag single box · drop cross-lane for L↔R
        </span>
      </div>
      <div className="relative flex-1 min-h-0 w-full"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={`${testid}-dropzone`}>
        {svg
          ? <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: svg }} />
          : <div className="h-full w-full flex items-center justify-center text-xs text-slate-600">Empty lane — drag pallets here from the other side.</div>}
        <div className="absolute inset-0 pointer-events-none">
          {rects.map((r) => {
            const isSrc = dragging && dragging.lane === r.lane && dragging.arr_index === r.arr_index && dragging.layer_index === r.layer_index;
            const kindBorder = r.is_floor ? "border-emerald-400/60" : "border-blue-400/60";
            const kindLabel = r.is_floor ? "STACK" : `L${r.layer_index + 1}`;
            return (
              <div key={`${r.arr_index}-${r.layer_index}`}
                draggable
                onDragStart={(e) => onDragStart(e, r)}
                onDragEnd={onDragEnd}
                title={`${r.product_code} — ${r.is_floor ? "drag entire stack (all layers move together)" : "drag this single pallet"}`}
                data-testid={`sandbox-layer-${lane}-${r.arr_index}-${r.layer_index}`}
                className={`absolute pointer-events-auto cursor-grab active:cursor-grabbing rounded-sm border-2 border-dashed hover:bg-white/10 hover:border-solid transition-colors ${kindBorder} ${isSrc ? "opacity-30" : "opacity-90"}`}
                style={{
                  left: `${r.left_pct}%`, top: `${r.top_pct}%`,
                  width: `${r.width_pct}%`, height: `${r.height_pct}%`,
                }}>
                {r.width_pct > 3 && r.height_pct > 3 && (
                  <span className="absolute top-0 left-0 px-1 py-0.5 text-[8px] font-mono font-bold text-white bg-slate-950/70 rounded-br pointer-events-none">
                    {kindLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {dropHint && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold font-mono animate-pulse">
              DROP TO PLACE (no revert · physics will recompute)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Apply a sandbox move to an existing arrangement. This is a pure function so it
// can be reused by tests.
//
// Two drop kinds (decided from `drop_along_in` alone):
//
//   Empty-area drop  — drop_along_in falls OUTSIDE every target-lane stack
//   ─────────────────────────────────────────────────────────────────────
//     • Floor source (is_floor)  → the ENTIRE source stack moves as one unit
//     • Upper source (!is_floor) → only that single pallet moves
//     • Reflow is implicit: build_manual_plan lays arrangement in order from
//       the container nose, so a floor gap auto-compacts toward coord 0.
//
//   On-top drop      — drop_along_in falls INSIDE an existing target stack
//   ─────────────────────────────────────────────────────────────────────
//     • ALWAYS transfers exactly ONE pallet to the top of the target stack.
//       "Floor-to-Layer" stacking — it does NOT matter if the source was a
//       floor pallet: the user's gesture "drop on top of X" always means
//       "stack a single pallet on top of X".
//     • Floor source: the FLOOR pallet is picked up, upper pallets of the
//       source stack SLIDE DOWN (layer 1 becomes floor, etc.).
//     • Upper source: that specific pallet is picked up.
//     • Dropping onto your OWN stack: no-op for floor drag; cycle-to-top for
//       upper-layer drag.
export function applySandboxMove(arrangement, sourcePlacement, source, targetLane, drop_along_in) {
  const arr = {
    A: [...((arrangement && arrangement.A) || [])],
    B: [...((arrangement && arrangement.B) || [])],
  };
  const srcLane = source.lane === "B" ? "B" : "A";
  const tgtLane = targetLane === "B" ? "B" : "A";

  // Snapshot the target lane's rendered geometry BEFORE we mutate anything —
  // the on-top / insert decision uses the currently visible layout.
  const targetPlacement = (sourcePlacement || [])
    .filter((p) => p.lane === tgtLane)
    .slice()
    .sort((a, b) => (a.start_in || 0) - (b.start_in || 0));

  let onTopArrIdx = null;
  let insertIdx = targetPlacement.length;
  for (let i = 0; i < targetPlacement.length; i++) {
    const p = targetPlacement[i];
    if (drop_along_in < (p.start_in || 0)) { insertIdx = i; break; }
    if (drop_along_in >= (p.start_in || 0) && drop_along_in <= (p.end_in || 0)) {
      onTopArrIdx = p.arr_index != null ? p.arr_index : i;
      insertIdx = i + 1;
      break;
    }
  }

  const dropOnTop = onTopArrIdx != null;
  const sameSrcAsOnTop = dropOnTop && source.lane === targetLane && source.arr_index === onTopArrIdx;

  const srcStack = arr[srcLane][source.arr_index];
  if (!srcStack) return arrangement;   // stale drag — arrangement changed since dragstart
  // Preserve any rotated flag on the source column so a rotated stack stays
  // rotated when the whole stack moves. Single-pallet moves land on the target
  // column so they follow the TARGET's rotation, not the source's.
  const srcRotated = !!srcStack.rotated;

  // -------- No-op / cycle-to-top when the drop lands on the source stack --------
  if (sameSrcAsOnTop) {
    if (source.is_floor) return arrangement;    // floor drag onto its own stack → no move
    const layers = [...(srcStack.layers || [])];
    if (source.layer_index >= layers.length) return arrangement;
    const [layer] = layers.splice(source.layer_index, 1);
    layers.push(layer);                          // cycle the picked pallet to the top
    arr[srcLane][source.arr_index] = { ...srcStack, layers };
    return arr;
  }

  // -------- Pull the moving pallet(s) out of the source stack --------
  let movingLayers;
  let movingRotated = false;
  let sourceStackRemoved = false;
  if (dropOnTop) {
    // On-top drop always transfers exactly ONE pallet — regardless of source.is_floor.
    if (source.is_floor) {
      // Take the floor pallet; the rest of the source column slides down.
      const layers = [...(srcStack.layers || [])];
      if (!layers.length) return arrangement;
      movingLayers = [layers.shift()];
      if (layers.length === 0) { arr[srcLane].splice(source.arr_index, 1); sourceStackRemoved = true; }
      else arr[srcLane][source.arr_index] = { ...srcStack, layers };
    } else {
      const layers = [...(srcStack.layers || [])];
      if (source.layer_index >= layers.length) return arrangement;
      movingLayers = [layers[source.layer_index]];
      layers.splice(source.layer_index, 1);
      if (layers.length === 0) { arr[srcLane].splice(source.arr_index, 1); sourceStackRemoved = true; }
      else arr[srcLane][source.arr_index] = { ...srcStack, layers };
    }
  } else if (source.is_floor) {
    // Empty-area drop of a floor pallet: whole stack moves as a unit — with rotation.
    movingLayers = [...(srcStack.layers || [])];
    movingRotated = srcRotated;
    arr[srcLane].splice(source.arr_index, 1);
    sourceStackRemoved = true;
  } else {
    // Empty-area drop of an upper layer: single pallet moves; upper pallets slide down.
    const layers = [...(srcStack.layers || [])];
    if (source.layer_index >= layers.length) return arrangement;
    movingLayers = [layers[source.layer_index]];
    layers.splice(source.layer_index, 1);
    if (layers.length === 0) { arr[srcLane].splice(source.arr_index, 1); sourceStackRemoved = true; }
    else arr[srcLane][source.arr_index] = { ...srcStack, layers };
  }
  if (!movingLayers.length) return arrangement;

  // If the source pull deleted a stack in the SAME lane at/below the target index,
  // downstream arr_index values shifted by 1 — compensate so the drop lands where
  // the user aimed relative to the ORIGINAL layout.
  if (sourceStackRemoved && source.lane === targetLane) {
    if (source.arr_index < insertIdx) insertIdx = Math.max(0, insertIdx - 1);
    if (onTopArrIdx != null && source.arr_index < onTopArrIdx) onTopArrIdx = onTopArrIdx - 1;
  }

  if (dropOnTop && arr[tgtLane][onTopArrIdx]) {
    // Add as the new TOP layer of the target stack (bottom = index 0, top = last).
    const t = arr[tgtLane][onTopArrIdx];
    arr[tgtLane][onTopArrIdx] = { ...t, layers: [...(t.layers || []), movingLayers[0]] };
  } else {
    // New column at the drop position — build_manual_plan will reflow it toward the nose.
    // Carry the source's rotated flag on WHOLE-STACK moves.
    const newCol = movingRotated ? { layers: movingLayers, rotated: true } : { layers: movingLayers };
    arr[tgtLane].splice(insertIdx, 0, newCol);
  }
  return arr;
}
